import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { operationTypes } from "@/types/operationTypes";
import { ParameterSubjectType, ParameterInputType } from "@/types/parameterTypes";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { ParameterItemRunValue } from "../parameters/ParameterItemRun";
import { ReferenceLabel } from "../parameters/ReferenceLabel";
import { NotesItemRun } from "./NotesItemRun";
import { Operation } from "@/backend/models/operation";
import { Form } from "react-bootstrap";
import { buildInterpolatedString } from "../parameters/ParameterReference";
import { TextItemRun } from "./TextItemRun";
import { CodeItemRun } from "../codes/CodeItemRun";
import { CommandingItemRun } from "./CommandingItemRun";
import { GraphItem } from "./graphs/GraphItem";
import { EquipmentOperationRun } from "./EquipmentOperationRun";
import { useIsSnippetPreview } from "../containers/SnippetPreviewContext";
import { I18n } from "@/i18n/I18n";
import { CalloutIcon } from "./CalloutIcon";

export function OperationRun(props: { operation: Operation }) {
  const { operation } = props;

  const idForScroll = useTreeStore((state) => state.idForScroll);
  const idForScrollFound = useTreeStore((state) => state.idForScrollFound);
  const setIdForScrollFound = useTreeStore((state) => state.setIdForScrollFound);

  useEffect(() => {
    if (idForScroll === operation.id && !idForScrollFound) {
      setIdForScrollFound(true);
    }
  }, [idForScroll, operation.id, setIdForScrollFound]);

  let textClassname = '';
  if (operation.type === operationTypes.Warning) textClassname = 'bg-warning-subtle border border-warning p-3 rounded';
  if (operation.type === operationTypes.Danger) textClassname = 'bg-danger-subtle border border-danger p-3 rounded';
  if (operation.type === operationTypes.Information) textClassname = 'bg-info-subtle border border-info p-3 rounded';

  const isCallout = operation.isCallout();

  const body = (
    <div className="d-flex flex-column gap-1 flex-grow-1">
      <div className=''>
        <div className='d-flex flex-grow justify-content-between'>
          <div className="d-flex gap-1">
            { operation.title &&
              <h6 className={isCallout ? 'mb-0' : undefined}>{operation.title}</h6>
            }
          </div>
        </div>
      </div>
      <div className='flex-grow'>
        <OperationContent operation={operation} />
      </div>
    </div>
  );

  return (
    <div id={operation.id} className={`observed-element ${textClassname}`} key={operation.id}>
      { isCallout
        // The icon sits in a gutter alongside the whole callout rather than beside the body
        // text, so it reads as a marker for the callout instead of a bullet on one line.
        ? <div className="d-flex align-items-start gap-2">
            <CalloutIcon type={operation.type} size={18} />
            {body}
          </div>
        : body }
    </div>
  );
}

// Commanding talks to instruments, code executes Python, and equipment resolves against a
// procedure instance's equipment items. None of that exists in a snippet preview, so they are
// shown in place but plainly marked as inert.
function InertOperation(props: { operation: Operation }) {
  const { operation } = props;

  return (
    <div className='card bg-body-tertiary border-secondary-subtle'>
      <div className='card-body py-2 d-flex align-items-center justify-content-between gap-2'>
        <code>{operation.content || operation.title || operation.type}</code>
        <span className='badge bg-secondary text-nowrap'>{I18n.get('notExecutableInPreview', { type: operation.type })}</span>
      </div>
    </div>
  );
}

function OperationContent(props: { operation: Operation }) {
  const { operation } = props;
  const isSnippetPreview = useIsSnippetPreview();
  const [parameter] = useParameterStore(useShallow(state => state.parametersForSubject(operation.id, ParameterSubjectType.OPERATION)));
  if (operation.isText() ) return <TextItemRun initItem={operation} />;
  if (operation.isNotes()) return <NotesItemRun initItem={operation} />;
  if (isSnippetPreview && (operation.isCode() || operation.isCommanding() || operation.isEquipment())) {
    return <InertOperation operation={operation} />;
  }
  if (operation.isCode()) return <CodeItemRun operation={operation} />;
  if (operation.isCommanding()) return <CommandingItemRun operation={operation} />;
  if (operation.isEquipment()) return <EquipmentOperationRun operation={operation} />;
  if (operation.hasParameter() && parameter) {
    if (operation.isGraph()) return <GraphItem operation={operation} runView />;
    const contentAsDescription = parameter?.inputType === ParameterInputType.TABLE || operation.isFileType();
    return (
      <div className='w-100' id={parameter.id}>
        { contentAsDescription && <>
          <p style={{ whiteSpace: 'pre-wrap' }}>{buildInterpolatedString(operation.content, operation)}</p>
          <ReferenceLabel consumerId={parameter.id} />
        </> }
        { !contentAsDescription && <div className='d-flex gap-1'>
          {operation.content && <Form.Label className="col-form-label col-form-label-sm">{operation.content}</Form.Label>}
          <ReferenceLabel consumerId={parameter.id} />
        </div>}
        <ParameterItemRunValue parameter={parameter} />
      </div>
    );
  }
  return null;

}
